import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import SwiperCore, { FreeMode, Navigation } from 'swiper'
import { RailItem } from '../RailItem/RailItem'
import { ICategoryTreeItem, ILightRailItem } from '../../../types'
import { useOttOverlay } from '../../../contexts'
import {
  CustomPosterSwiper,
  CustomSwiper,
  RailTitle,
} from '../MainRailPage/MainRailPage.style'
import { SeparateRailWrap } from './ComponentsFactory.style'
import { RailItemRegularProgram } from '../RailItemRegularProgram/RailItemRegularProgram'
import { MainRail } from '../Rails'
import { fetchRailData } from '../../Api'
import { IComponents } from '../../types'
import { prettyLog } from '../../../utils'
import { useRouter } from 'next/router'
import { getRailConfig } from './Helpers'
import { NextButton, PrevButton } from '../SideButtons/SideButtons'
import { FullPoster } from './FullPoster'
import { config } from '../../../lib'
import { MinuteLyRail } from '../MinuteLy/MinuteLy'

SwiperCore.use([Navigation, FreeMode])

interface RailProps {
  data: ICategoryTreeItem | null
  index: number
}

export default function ComponentsFactory({ data, index }: RailProps) {
  const fetching = useRef(false)
  const [init, setInit] = useState(false)
  const { rails } = useOttOverlay()
  const [railItems, setRailItems] = useState<ILightRailItem[] | null>()
  const [nextButton, setNextButton] = useState<boolean>(true)
  const [prevButton, setPrevButton] = useState<boolean>(false)
  const router = useRouter()
  const { ks } = useOttOverlay()
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToTop()
  }, [])

  const { compName, id }: { compName: IComponents | null; id: number } =
    useMemo(() => {
      const toReturn = { compName: null as IComponents, id: 0 }
      const component_type =
        data?.dynamicData?.componant_type ||
        data?.dynamicData?.componennt_type ||
        data?.dynamicData?.component_type

      if (component_type) {
        toReturn.compName =
          (component_type?.value.trim() as IComponents) || null
      } else {
        prettyLog(
          'ComponentsFactory: 38',
          { msg: 'missing component_type', data: data?.dynamicData },
          'warning'
        )
      }

      if (data?.unifiedChannels && data?.unifiedChannels.length > 0) {
        try {
          toReturn.id = data?.unifiedChannels[0].id
        } catch (e) {
          prettyLog('ComponentsFactory: 52', e, 'error')
        }
      } else {
        prettyLog(
          'ComponentsFactory: 67',
          { msg: 'missing id', data: data },
          'error'
        )
      }
      return toReturn
    }, [data])

  useMemo(() => {
    if (id) {
      const r = rails?.find(r => {
        return r.id === id
      })
      if (r) {
        setRailItems(r.child)
      }
    }
  }, [id, rails])

  useEffect(() => {
    if (
      ks &&
      id &&
      (!railItems || railItems.length === 0) &&
      !fetching.current
    ) {
      try {
        fetching.current = true
        fetchRailData({
          railId: id,
          ks: ks,
          kaltura_end_point: config.kaltura_end_point,
        }).then(({ response }) => {
          fetching.current = false
          if (response) {
            setRailItems(response)
          } else {
            prettyLog(
              'ComponentsFactory: 71',
              { msg: 'failed rail data fetch', id },
              'error'
            )
          }
        })
      } catch (e) {
        prettyLog('ComponentsFactory: 93', e, 'error')
        router.reload()
      }
    }
  }, [id, ks, railItems, router])

  useEffect(() => {
    if (compName && railItems && railItems.length > 0) {
      const railConfig = getRailConfig({ railName: compName })
      if (railConfig.size && railItems.length < railConfig.size.pageSize) {
        setNextButton(false)
        setPrevButton(false)
      }
    }
  }, [compName, railItems])

  const swiperConfig = useMemo(() => {
    if (compName) {
      const railConfig = getRailConfig({
        railName: compName,
      })

      const railSpeed = 900

      return {
        rtl: 'true',
        slidesPerView: railConfig?.media?.slidesPerView,
        spaceBetween: railConfig?.media?.spaceBetween,
        freeMode: true,
        watchSlidesProgress: true,
        navigation: {
          nextEl: '.swiper-button-next-custom',
          prevEl: '.swiper-button-prev-custom',
        },
        speed: railSpeed,
        breakpoints: {
          430: {
            slidesPerView: railConfig?.media?.slidesPerView,
            spaceBetween: railConfig?.media?.spaceBetween,
            centeredSlides: false,
            ...(compName !== 'main' && {}),
            allowSlidePrev: true,
            allowSlideNext: true,
            ...railConfig['430'],
          },
          769: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 15,
            ...railConfig['769'],
          },
          1280: {
            allowTouchMove: false,
            slidesPerView: 4,
            slidesPerGroup: 4,
            spaceBetween: 20,
            ...railConfig['1280'],
          },
          1440: {
            slidesPerView: 5,
            slidesPerGroup: 5,
            spaceBetween: 25,
            ...railConfig['1440'],
          },
        },
        onInit: () => {
          setInit(true)
        },
      }
    }
    return {}
  }, [compName])

  if (!railItems || railItems.length === 0) {
    return null
  }

  const mainRail = () => {
    const showItemTitle = data?.dynamicData.title?.value !== 'false'
    const showComponentTitle =
      data?.dynamicData.show_componant_title?.value !== 'false'

    return (
      <SeparateRailWrap
        programWidth={90}
        data-type={compName}
        data-id={data?.unifiedChannels[0].id}
        data-item-title={showItemTitle ? 'show' : 'hide'}
        data-comp-title={showComponentTitle ? 'show' : 'hide'}
        showItemTitle={showItemTitle}
        theme={{ show: init, isFirst: index === 0 }}
      >
        {data && showComponentTitle && (
          <RailTitle>{data.name.trim()}</RailTitle>
        )}
        <CustomSwiper
          {...swiperConfig}
          onReachEnd={() => {
            setNextButton(false)
            setPrevButton(true)
          }}
          onReachBeginning={() => {
            setPrevButton(false)
            setNextButton(true)
          }}
        >
          <PrevButton
            prevButton={prevButton}
            toggleNextButton={nextButton => setNextButton(nextButton)}
          />
          {railItems?.map((r, i) => (
            <SwiperSlide key={r.id}>
              <RailItem
                data={r}
                isFirst={i === 0}
                isLast={i === railItems.length - 1}
                showItemTitle={showItemTitle}
              />
            </SwiperSlide>
          ))}
          <NextButton
            nextButton={nextButton}
            togglePrevButton={prevButton => setPrevButton(prevButton)}
          />
        </CustomSwiper>
      </SeparateRailWrap>
    )
  }

  const catchupRail = () => {
    const showItemTitle = data?.dynamicData.title?.value !== 'false'
    return (
      <SeparateRailWrap
        programWidth={90}
        data-type={compName}
        theme={{ show: init }}
        className={'catchup'}
      >
        {data && <RailTitle>{data.name.trim()}</RailTitle>}
        <CustomSwiper
          {...swiperConfig}
          onReachEnd={() => {
            setNextButton(false)
            setPrevButton(true)
          }}
          onReachBeginning={() => {
            setPrevButton(false)
            setNextButton(true)
          }}
        >
          <PrevButton
            prevButton={prevButton}
            toggleNextButton={nextButton => setNextButton(nextButton)}
          />
          {railItems?.map((r, i) => (
            <SwiperSlide key={r.id}>
              <RailItem
                compName={compName}
                data={r}
                isFirst={i === 0}
                isLast={i === railItems.length - 1}
                showItemTitle={showItemTitle}
                timeDuration={true}
              />
            </SwiperSlide>
          ))}
          <NextButton
            nextButton={nextButton}
            togglePrevButton={prevButton => setPrevButton(prevButton)}
          />
        </CustomSwiper>
      </SeparateRailWrap>
    )
  }

  const posterRail = () => {
    if (!data) {
      return null
    }

    const showCompTitle =
      data.dynamicData.show_componant_title?.value !== 'false'
    const showItemTitle = data.dynamicData.title?.value !== 'false'
    if (!compName) {
      return null
    }
    switch (compName) {
      case 'fullposter':
        return (
          <FullPoster
            railItems={railItems}
            compName={compName}
            showItemTitle={showItemTitle}
            data={data}
            data-id={data?.unifiedChannels[0].id}
          />
        )
      default:
        return (
          <SeparateRailWrap
            programWidth={91}
            data-type={compName}
            data-id={data?.unifiedChannels[0].id}
            theme={{ show: init }}
          >
            {showCompTitle && <RailTitle>{data.name.trim()}</RailTitle>}
            <CustomPosterSwiper
              {...swiperConfig}
              onReachEnd={() => {
                setNextButton(false)
                setPrevButton(true)
              }}
              onReachBeginning={() => {
                setPrevButton(false)
                setNextButton(true)
              }}
            >
              <PrevButton
                prevButton={prevButton}
                toggleNextButton={nextButton => setNextButton(nextButton)}
              />
              {railItems?.map((r, i) => (
                <SwiperSlide key={r.id}>
                  <RailItemRegularProgram
                    key={r.id}
                    data={r}
                    railType={compName}
                    isFirst={i === 0}
                    isLast={i === railItems.length - 1}
                    showItemTitle={showItemTitle}
                  />
                </SwiperSlide>
              ))}
              <NextButton
                nextButton={nextButton}
                togglePrevButton={prevButton => setPrevButton(prevButton)}
              />
            </CustomPosterSwiper>
          </SeparateRailWrap>
        )
    }
  }

  if (!(data && compName)) {
    prettyLog(
      'ComponentsFactory: 371',
      { msg: 'missing component name', data: data },
      'error'
    )
    return (
      <div
        style={{
          color: '#fff',
          outline: '1px solid #fff',
          textAlign: 'left',
          direction: 'ltr',
        }}
      >
        <div>
          {data?.dynamicData?.componant_type?.value ||
            'missing "data.dynamicData.componant_type.value"'}
        </div>
        <div>{data?.id}</div>
        <div>'{data?.name}'</div>
      </div>
    )
  }

  switch (compName) {
    case 'main':
      return (
        <MainRail
          id={id as number}
          swiperConfig={swiperConfig}
          items={railItems}
        />
      )
    case 'personal_rail':
    case '16x9_regular':
      return mainRail()
    case 'catchup':
      return catchupRail()
    case 'poster_regular':
    case 'poster_small':
    case 'fullposter':
      return posterRail()
    case 'mintely':
      return <MinuteLyRail data={data} items={railItems} key={data.id} />
    default:
      return (
        <div
          datatype={'default'}
          style={{ color: '#fff', outline: '1px solid #fff', display: 'none' }}
        >
          <div>{data.dynamicData.componant_type?.value || ''}</div>
          <div>{data?.id}</div>
          <div>'{data?.name}'</div>
        </div>
      )
  }
}
